import {
    PDFViewer,
    PreservedText,
    ProviderClaimTransmission,
    ProviderDocumentUrl,
    ProviderPayer,
    ProviderSubscriber,
    Spinner,
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { RootStore } from 'stores';
import * as S from './claim-timeline-table.styles';

interface ITransmissionDetailProps {
    transmission: ProviderClaimTransmission;
}

const LinePlaceholder = React.memo(() => {
    return (
        <ContentLoader speed={2} height={20} style={{ width: '100%' }} preserveAspectRatio="">
            <rect x="0" y="0" rx="0" ry="0" width="50%" height="20" />
        </ContentLoader>
    );
});

export const TransmissionDetail: React.FunctionComponent<ITransmissionDetailProps> = inject('rootStore')(
    observer(
        (props: ITransmissionDetailProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { claimStore } = rootStore;
            const { transmission } = props;

            const [loading, setLoading] = useState(false);
            const [payer, setPayer] = useState<ProviderPayer | undefined>();
            const [subscriber, setSubscriber] = useState<ProviderSubscriber | undefined>();
            const [documentLoading, setDocumentLoading] = useState(false);
            const [documentUrl, setFile] = useState<ProviderDocumentUrl | undefined>();
            const id = transmission.documentId;

            useEffect(() => {
                (async () => {
                    if (!id) {
                        return;
                    }

                    setDocumentLoading(true);

                    try {
                        const res = await claimStore.loadDocumentDownloadUrl(id);
                        setFile(res);
                    } finally {
                        setDocumentLoading(false);
                    }
                })();
            }, [id]);

            useEffect(() => {
                (async (): Promise<void> => {
                    if (transmission && transmission.payerSubscriberId) {
                        setLoading(true);
                        const payerSubscriber = await claimStore.findPayerSubscriber(transmission.payerSubscriberId);
                        if (payerSubscriber) {
                            setPayer(payerSubscriber.payer);
                            setSubscriber(payerSubscriber.subscriber);
                        }
                        setLoading(false);
                    }
                })();
            }, [transmission, claimStore]);

            const renderMessages = (): React.ReactElement => {
                if (!!transmission.statuses?.length) {
                    return (
                        <S.ExpandTBody>
                            <S.ExpandTR>
                                <S.ExpandTD>
                                    <S.ExpandTitle>Payer Responses</S.ExpandTitle>
                                </S.ExpandTD>
                            </S.ExpandTR>

                            <S.ExpandTR>
                                <S.ExpandSubTH>Status</S.ExpandSubTH>
                                <S.ExpandSubTH>Code</S.ExpandSubTH>
                                <S.ExpandSubTH>Message</S.ExpandSubTH>
                            </S.ExpandTR>
                            {transmission.statuses.map((status, i) => (
                                <S.ExpandTR key={i}>
                                    <S.ExpandTD>{status.status}</S.ExpandTD>
                                    <S.ExpandTD>{status.messageCode}</S.ExpandTD>
                                    <S.ExpandTD>{status.messages}</S.ExpandTD>
                                </S.ExpandTR>
                            ))}
                        </S.ExpandTBody>
                    );
                }
                // const rows = _.map(messages, (message, i) => (
                //     <S.ExpandTR key={i}>
                //         {/* <S.ExpandTD>{message}</S.ExpandTD> */}
                //         <S.ExpandTD colSpan={3}>{messages}</S.ExpandTD>
                //     </S.ExpandTR>
                // ));
                // return _.isEmpty(messages) ? (
                //     <></>
                // ) : (
                //     <>
                //         <S.ExpandTR>
                //             <S.ExpandTD colSpan={4}>
                //                 <S.ExpandTitle>Payer Responses</S.ExpandTitle>
                //             </S.ExpandTD>
                //         </S.ExpandTR>
                //         <S.ExpandTR>
                //             <S.ExpandSubTH>Status</S.ExpandSubTH>
                //             <S.ExpandSubTH colSpan={3}>Message</S.ExpandSubTH>
                //         </S.ExpandTR>
                //         <S.ExpandTR>
                //             <S.ExpandTD>{status}</S.ExpandTD>
                //             <S.ExpandTD colSpan={3}>{messages}</S.ExpandTD>
                //         </S.ExpandTR>
                //     </>
                // );
                return <></>;
            };

            return (
                <S.Wrapper>
                    <S.ExpandTable key={transmission.id} expand={true}>
                        <S.ExpandTBody>
                            {loading && (
                                <S.ExpandTR>
                                    <S.ExpandTD colSpan={3}>
                                        <Spinner />
                                    </S.ExpandTD>
                                </S.ExpandTR>
                            )}
                            {!loading && (
                                <>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Payer</S.ExpandTH>
                                        <S.ExpandTD colSpan={3}>{payer && payer.name}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Subscriber</S.ExpandTH>
                                        <S.ExpandTD colSpan={3}>
                                            {subscriber &&
                                                `${_.compact([
                                                    subscriber.first,
                                                    subscriber.middle,
                                                    subscriber.last,
                                                ]).join(' ')}`}
                                        </S.ExpandTD>
                                    </S.ExpandTR>
                                    {transmission.type && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>Type</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.type}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                    {transmission.x12 && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>X12</S.ExpandTH>
                                            <S.ExpandTD colSpan={6}>
                                                <PreservedText
                                                    text={transmission.x12.replace(/~/g, '~\n')}
                                                    rows={transmission.x12.split('~').length}
                                                />
                                            </S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                    {transmission.externalId && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>External ID</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.externalId}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                    {transmission.payerIcn && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>Payer ICN</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.payerIcn}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                </>
                            )}
                        </S.ExpandTBody>
                        {renderMessages()}
                        {documentLoading && <LinePlaceholder />}
                    </S.ExpandTable>
                    {documentUrl?.url && <PDFViewer url={documentUrl.url} />}
                </S.Wrapper>
            );
        },
    ),
);
